import { useEffect, useRef, useState } from "react";
import { Play } from "untitledui-js/react";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "react-i18next";

import { VideoProps } from "../types/VideoTypes";
import { useGetSubtitles } from "@hooks/useGetSubtitles";
import { languages } from "../constants";

function CaptionTrack({
  assetUrl,
  language,
}: Readonly<{ assetUrl: string; language: string }>) {
  const { data: vttContent } = useGetSubtitles(assetUrl);
  const [trackSrc, setTrackSrc] = useState<string>();

  useEffect(() => {
    if (!vttContent) return;

    const objectUrl = URL.createObjectURL(
      new Blob([vttContent], { type: "text/vtt" })
    );
    setTrackSrc(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [vttContent]);

  if (!trackSrc) return null;

  return (
    <track
      kind="captions"
      src={trackSrc}
      srcLang={language}
      label={languages[language as keyof typeof languages] ?? language}
    />
  );
}

export default function Video(props: Readonly<VideoProps>) {
  const { video, title, poster, captions } = props;
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  if (!video?.filename) return null;

  const validCaptions = (captions ?? []).filter((c) => c.caption?.filename);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.error("Error playing video:", error);
      });
      setIsPlaying(true);
    }
  };

  return (
    <div className="relative aspect-video w-full">
      {/* Since we provide captions dynamically, we disable the media-has-caption rule */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        src={video.filename}
        title={title}
        poster={poster?.filename}
        controls={isPlaying}
        preload="none"
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
        className="aspect-video w-full object-cover"
      >
        {validCaptions.map((c) => (
          <CaptionTrack
            key={c._uid}
            assetUrl={c.caption.filename}
            language={c.language}
          />
        ))}
      </video>
      <div
        className={twMerge(
          "absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-500",
          isPlaying ? "pointer-events-none opacity-0" : "opacity-100"
        )}
      >
        <button
          onClick={handlePlay}
          className="flex h-12 items-center rounded-full bg-secondary-700 pl-5 font-semibold text-primary-500"
          aria-label={t("Global.playVideo")}
        >
          Play video{" "}
          <span className="mr-1 ml-1.5 flex h-10 w-10 items-center justify-center rounded-full bg-primary-500">
            <Play className="h-5 w-5 stroke-secondary-700" />
          </span>
        </button>
      </div>
    </div>
  );
}
